import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Select                                                            */
/* ------------------------------------------------------------------ */

export interface SelectProps
  extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  hint?: string;
  error?: string;
  selectSize?: "sm" | "md" | "lg";
}

const css = /* css */ `
  .sf-select-wrap {
    position: relative;
    display: flex;
  }

  .sf-select {
    display: block;
    width: 100%;
    border: 1px solid var(--sf-border);
    border-radius: var(--sf-radius-md);
    background: var(--sf-bg-elevated);
    color: var(--sf-text);
    font-family: var(--sf-font-sans);
    appearance: none;
    padding-right: 32px;
    transition: border-color var(--sf-transition-fast),
                box-shadow var(--sf-transition-fast);
    cursor: pointer;
  }

  .sf-select:hover { border-color: var(--sf-border-strong); }

  .sf-select:focus-visible {
    outline: var(--sf-ring-width) solid var(--sf-ring-color);
    outline-offset: var(--sf-ring-offset);
    border-color: var(--sf-border-focus);
  }

  .sf-select:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: var(--sf-bg-inset);
  }

  /* Sizes */
  .sf-select--sm { font-size: var(--sf-text-sm); padding: 6px 32px 6px 10px; height: 30px; }
  .sf-select--md { font-size: var(--sf-text-base); padding: 8px 32px 8px 12px; height: 36px; }
  .sf-select--lg { font-size: var(--sf-text-md); padding: 10px 36px 10px 14px; height: 42px; }

  /* Error */
  .sf-select--error { border-color: var(--sf-error); }
  .sf-select--error:focus-visible {
    outline-color: rgba(201, 42, 42, 0.4);
    border-color: var(--sf-error);
  }

  /* Chevron */
  .sf-select-chevron {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: var(--sf-text-muted);
  }
`;

const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      hint,
      error,
      selectSize = "md",
      className,
      id: idProp,
      children,
      ...rest
    },
    ref,
  ) => {
    injectStyles("sf-select", css);

    const autoId = React.useId();
    const id = idProp ?? autoId;
    const hintId = hint ? `${id}-hint` : undefined;
    const errorId = error ? `${id}-error` : undefined;

    const selectClasses = [
      "sf-select",
      `sf-select--${selectSize}`,
      error ? "sf-select--error" : "",
      className ?? "",
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div className="sf-field">
        {label && (
          <label className="sf-field__label" htmlFor={id}>
            {label}
          </label>
        )}
        <div className="sf-select-wrap">
          <select
            ref={ref}
            id={id}
            className={selectClasses}
            aria-invalid={error ? true : undefined}
            aria-describedby={
              [hintId, errorId].filter(Boolean).join(" ") || undefined
            }
            {...rest}
          >
            {children}
          </select>
          <svg
            className="sf-select-chevron"
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="4 6 8 10 12 6" />
          </svg>
        </div>
        {hint && !error && (
          <span id={hintId} className="sf-field__hint">
            {hint}
          </span>
        )}
        {error && (
          <span id={errorId} className="sf-field__error" role="alert">
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="currentColor"
              aria-hidden="true"
            >
              <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 3.75a.75.75 0 011.5 0v3.5a.75.75 0 01-1.5 0v-3.5zM8 11a1 1 0 100 2 1 1 0 000-2z" />
            </svg>
            {error}
          </span>
        )}
      </div>
    );
  },
);

Select.displayName = "Select";
export { Select };
